import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AiOutlineClose } from "react-icons/ai";
import { ImagePlus } from "lucide-react";
import { toast } from "react-toastify";
import { uploadAssestsV2Action } from "../../store/Languages/LanguageAction";
import { saveYoutubePlanRequest, fetchUserJobsRequest } from "../../store/SermonStudies/sermonStudiesAction";
import { readLanguageAction } from "../../store/Languages/LanguageAction";
import LanguageDrawer from "../../components/universal/LanguageDrawer";
import CustomTabsV2 from "../../components/universal/CustomTabsV2";
import CollapsibleRightPanel from "../../components/universal/CollapsibleRightPanel";

const isRenderableImageUrl = (value) => typeof value === "string" && /^https?:\/\//i.test(value);

const getSermonPlanImageUrl = (plan = {}) =>
  plan?.gallery?.imageUrl ||
  plan?.gallery?.mobileImageUrl ||
  plan?.gallery?.tabImageUrl ||
  (isRenderableImageUrl(plan?.plansCoverImageUrl) ? plan.plansCoverImageUrl : "") ||
  (isRenderableImageUrl(plan?.planThumbnailUrl) ? plan.planThumbnailUrl : "") ||
  (isRenderableImageUrl(plan?.thumbnailUrl) ? plan.thumbnailUrl : "");

const SermonPlanForm = ({ church, typeofentity, onClose, onSuccess, planId }) => {
  const dispatch = useDispatch();

  const [planName, setPlanName] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("en");
  const [activeLanguageDetails, setActiveLanguageDetails] = useState({ language_code: "en", language_name: "English" });
  const [languagemeta, setLanguagemeta] = useState([]);
  const [isLanguageSave, setIsLanguageSave] = useState(false);
  const [languagesList, setLanguagesList] = useState([]);

  useEffect(() => {
    dispatch(readLanguageAction({
      apiPayloadRequest: { page: 1, limit: 100 },
      callback: (res) => {
        if (res.type === 1) {
          setLanguagesList(res.data || []);
        }
      }
    }));
  }, [dispatch]);

  useEffect(() => {
    if (planId) {
      setIsLoadingDetails(true);
      dispatch(fetchUserJobsRequest({ _id: planId }, (res) => {
        setIsLoadingDetails(false);
        if (res.type === 1 && res.data) {
          const planData = Array.isArray(res.data) ? res.data[0] : res.data;
          setPlanName(planData.planName || "");
          setYoutubeUrl(planData.youtubeLink || "");
          setThumbnailUrl(getSermonPlanImageUrl(planData));
        }
      }));
    }
  }, [dispatch, planId]);

  const handleCreatePlanFromYoutube = () => {
    if (!youtubeUrl?.trim()) return toast.error("Please paste a YouTube link");
    if (!thumbnailUrl?.trim()) return toast.error("Please provide a thumbnail image URL or upload one");
    if (!planName?.trim()) return toast.error("Please enter a plan name");

    setIsCreatingPlan(true);

    const entityObjId = church?._id || null;
    const entityFieldPayload = {};

    if (entityObjId && String(typeofentity) === "1") entityFieldPayload.churchObjId = entityObjId;
    if (entityObjId && String(typeofentity) === "2") entityFieldPayload.channelObjId = entityObjId;
    if (entityObjId && String(typeofentity) === "3") entityFieldPayload.ministriesObjId = entityObjId;

    const payload = {
      youtubelink: youtubeUrl.trim(),
      planName: planName.trim(),
      planThumbnailUrl: thumbnailUrl.trim(),
      scope: entityObjId ? "entity" : "global",
      typeofentity: entityObjId ? String(typeofentity || church?.typeofentity || "") : "",
      entityObjId: entityObjId || "",
      ...entityFieldPayload
    };

    dispatch(saveYoutubePlanRequest(payload, (res) => {
      setIsCreatingPlan(false);
      if (res.type === 1) {
        toast.success("Sermon Plan created successfully");
        onSuccess?.();
        onClose?.();
      } else {
        toast.error(res.message || "Failed to create Sermon Plan");
      }
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    dispatch(
      uploadAssestsV2Action({
        apiPayloadRequest: formData,
        callback: (res) => {
          setIsUploading(false);
          if (res?.url) {
            setThumbnailUrl(res.url);
            toast.success("Image uploaded successfully");
          } else {
            toast.error("Failed to upload image");
          }
        },
      })
    );
  };

  return (
    <div className="flex flex-col md:flex-row h-full w-full flex-1 rounded-2xl overflow-hidden">
      <div className="flex h-full flex-1">
        <LanguageDrawer
          languageData={languagesList}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setActiveLanguageDetails={setActiveLanguageDetails}
          languagemeta={languagemeta}
          setIsLanguageSave={setIsLanguageSave}
        />
        <div className="flex h-full flex-1">
          <div className="flex flex-col h-full w-full bg-greyishV2 bg-opacity-50 md:border-l md:border-r border-greyish border-opacity-30 overflow-auto">
            <div className="flex gap-4 items-center justify-between h-[64px] px-4 border-b border-[#28292a]">
              <p className="text-white font-semibold text-lg">
                {planId ? (activeTab === "en" ? "Edit Sermon Plan" : `Translate Sermon Plan to ${activeLanguageDetails?.language_name}`) : (activeTab === "en" ? "Add Sermon Plan" : `Translate Sermon Plan to ${activeLanguageDetails?.language_name}`)}
              </p>
              <div className="flex items-center gap-5">
                <button
                  onClick={handleCreatePlanFromYoutube}
                  disabled={!youtubeUrl || isCreatingPlan}
                  className={`flex items-center justify-center gap-3 font-semibold px-6 py-2 rounded-xl transition shadow-md text-[12px]
                    ${
                      isCreatingPlan
                        ? "bg-[#F8BA34]/40 cursor-not-allowed text-black"
                        : youtubeUrl
                        ? "bg-[#F8BA34] hover:bg-yellow-500 text-black"
                        : "bg-[#F8BA34]/40 cursor-not-allowed text-black"
                    }`}
                >
                  <span>{isCreatingPlan ? "Creating..." : "Save Sermon Plan"}</span>
                  {isCreatingPlan && (
                    <span className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  )}
                </button>
              </div>
            </div>

            <div className="px-6 py-6 h-full overflow-y-auto">
              {isLoadingDetails ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-4 border-[#F8BA34] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto">
                  <div className="flex flex-col gap-2 border-b border-gray-700 pb-2">
                    <label className="text-[12px] text-greyish font-medium">Plan Name *</label>
                    <input
                      type="text"
                      placeholder="Enter Plan Name"
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                      className="bg-transparent focus:outline-none text-lg text-white font-medium"
                    />
                  </div>

                  <div className="flex flex-col gap-2 border-b border-gray-700 pb-2">
                    <label className="text-[12px] text-greyish font-medium">YouTube URL *</label>
                    <input
                      type="text"
                      placeholder="Paste YouTube sermon link"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      className="bg-transparent focus:outline-none text-sm text-white w-full py-1"
                    />
                  </div>

                  <div className="flex flex-col gap-2 border-b border-gray-700 pb-2">
                    <label className="text-[12px] text-greyish font-medium">Plan Thumbnail URL *</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Enter Image URL or upload"
                        value={thumbnailUrl}
                        onChange={(e) => setThumbnailUrl(e.target.value)}
                        className="flex-1 bg-transparent focus:outline-none text-sm text-white py-1"
                      />
                      <div className="relative">
                        <input
                          type="file"
                          id="thumbnail-upload"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileUpload}
                        />
                        <label htmlFor="thumbnail-upload">
                          <ImagePlus
                            className={`cursor-pointer hover:text-yellowish transition-colors ${
                              isUploading ? "text-yellowish animate-pulse" : "text-white"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {thumbnailUrl && (
                    <div className="w-full flex justify-center py-4 bg-blackish bg-opacity-30 rounded-2xl border border-greyish border-opacity-10 relative">
                      <div className="relative group">
                        <img
                          src={thumbnailUrl}
                          alt="Thumbnail Preview"
                          className="w-full max-w-[400px] h-[250px] object-cover rounded-xl border-2 border-[#F8BA34] shadow-lg"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/400x250?text=Invalid+Image+URL";
                          }}
                        />
                        <button
                          onClick={() => setThumbnailUrl("")}
                          className="absolute top-2 right-2 bg-black/70 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                        >
                          <AiOutlineClose size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CollapsibleRightPanel title="Stats / Layouts">
        <CustomTabsV2
          tabHeight={"h-[64px]"}
          tabsTitleArray={["Stats", "Layouts"]}
          tabsContentObject={{
            Stats: (
              <div className="mt-8 flex items-center justify-center w-full text-white px-8 text-center text-sm text-greyish italic">
                No statistics available for new sermon plans.
              </div>
            ),
            Layouts: (
              <div className="mt-8 flex items-center justify-center w-full text-white px-8 text-center text-sm text-greyish italic">
                Save this sermon plan to manage its layout.
              </div>
            ),
          }}
        />
      </CollapsibleRightPanel>

      {/* No gallery modal needed */}
    </div>
  );
};

export default SermonPlanForm;
